import { AUDIO_ERROR_CODES, CHAT_ERROR_CODES } from '@shared/constants/error-codes';
import {
  AUDIO_ATTACHMENT_EXTENSIONS,
  AUDIO_TRANSCRIPTION_LIMITS,
} from '@shared/constants/audio-transcription';
import { extractChatErrorCode, getChatErrorMessage } from './chat-errors';
import { CHAT_DOCUMENT_MAX_SIZE_BYTES } from './chat-attachment-selection';

const MAX_NAMED_FILES = 3;
const MAX_FILENAME_LENGTH = 40;
const BYTES_PER_MEGABYTE = 1024 * 1024;
const BYTES_PER_GIGABYTE = 1024 * BYTES_PER_MEGABYTE;

export type RejectedChatAttachment = { code: string; filename: string };

function isUnsupportedFormatCode(code: string) {
  return code === CHAT_ERROR_CODES.CHAT_ATTACHMENT_UNSUPPORTED
    || code === AUDIO_ERROR_CODES.AUDIO_ATTACHMENT_UNSUPPORTED;
}

function shortenFilename(filename: string) {
  return filename.length > MAX_FILENAME_LENGTH
    ? `${filename.slice(0, MAX_FILENAME_LENGTH - 1)}…`
    : filename;
}

function formatUnsupportedFormatMessage(filenames: readonly string[]) {
  const named = filenames.slice(0, MAX_NAMED_FILES).map(shortenFilename).join(', ');
  const hidden = filenames.length - MAX_NAMED_FILES;
  const list = hidden > 0 ? `${named} et ${hidden} autre${hidden > 1 ? 's' : ''}` : named;

  return `${filenames.length > 1 ? 'Formats non supportés' : 'Format non supporté'} : ${list}`;
}

/**
 * One user-facing message per distinct rejection reason. Unsupported formats are grouped
 * and named ("Format non supporté : photo.heic"); other reasons keep their generic message.
 */
export function formatRejectedAttachmentMessages(rejected: readonly RejectedChatAttachment[]): string[] {
  const unsupported = rejected.filter((item) => isUnsupportedFormatCode(item.code));
  const messages = unsupported.length > 0
    ? [formatUnsupportedFormatMessage(unsupported.map((item) => item.filename))]
    : [];

  const otherCodes = new Set(
    rejected.filter((item) => !isUnsupportedFormatCode(item.code)).map((item) => item.code),
  );
  for (const code of otherCodes) {
    messages.push(getChatErrorMessage({ code }));
  }

  return messages;
}

/** Upload failure toast: a server-side format refusal names the file, like the client-side filter. */
export function getAttachmentUploadErrorMessage(error: unknown, filename: string) {
  const code = extractChatErrorCode(error);

  return code !== null && isUnsupportedFormatCode(code)
    ? formatUnsupportedFormatMessage([filename])
    : getChatErrorMessage(error);
}

/** Secondary lines of the drop overlay, derived from the existing format and limit constants. */
export function getChatDropHintLines(audioEnabled: boolean) {
  const lines = [
    `PDF, Word (.docx), Excel (.xlsx), 3D (.glb, .gltf, .obj, .stl, .ply, .3mf) · ${CHAT_DOCUMENT_MAX_SIZE_BYTES / BYTES_PER_MEGABYTE} Mo max · `
      + `${AUDIO_TRANSCRIPTION_LIMITS.maxDocumentFilesPerMessage} documents max`,
    'Maillages uniquement. Pas de CAO (STEP, IGES, DXF, DWG, formats natifs). Ressources externes, Draco et Basis non lus.',
  ];

  if (audioEnabled) {
    const audioFormats = AUDIO_ATTACHMENT_EXTENSIONS.map((extension) => extension.slice(1).toUpperCase()).join(', ');
    lines.push(
      `Audio : ${audioFormats} · ${AUDIO_TRANSCRIPTION_LIMITS.maxSizeBytes / BYTES_PER_GIGABYTE} Go max · `
        + `${AUDIO_TRANSCRIPTION_LIMITS.maxFilesPerMessage} fichiers max`,
    );
  }

  return lines;
}
